/**
 * Responsibility: get auto-open working, and refuse to claim it works until
 * it has been observed working.
 *
 * PLAN.md section 6.9 is emphatic about the last part. The toggle stays off
 * until a live test passes, and the test fires from a timer rather than from
 * the click that started it - a popup opened during a click handler proves
 * nothing about one opened from a WebSocket message.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useAutoOpen } from '../../hooks/useAutoOpen';

type WizardProps = {
  open: boolean;
  onClose: () => void;
  onVerified: () => void;
};

function browserInstructions(): { name: string; steps: string[] } {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) {
    return {
      name: 'Firefox',
      steps: [
        'Open Settings, then Privacy and Security.',
        'Under Permissions, find "Block pop-up windows" and choose Exceptions.',
        'Add this site and press Allow, then Save Changes.',
      ],
    };
  }
  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    return {
      name: 'Safari',
      steps: [
        'Open Safari, then Settings, then Websites.',
        'Select Pop-up Windows in the sidebar.',
        'Set this site to Allow.',
      ],
    };
  }
  return {
    name: 'Chrome or Edge',
    steps: [
      'Click the icon at the left of the address bar.',
      'Choose Site settings.',
      'Set Pop-ups and redirects to Allow, then reload.',
    ],
  };
}

export function PopupPermissionWizard({ open, onClose, onVerified }: WizardProps) {
  const [step, setStep] = useState(0);
  const [result, setResult] = useState<'untested' | 'pass' | 'fail'>('untested');
  const [permission, setPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied',
  );
  const { verify, requestNotificationPermission } = useAutoOpen(false);
  const guide = browserInstructions();

  const runTest = async () => {
    const passed = await verify();
    setResult(passed ? 'pass' : 'fail');
    if (passed) {
      onVerified();
      setStep(2);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Set up auto-open</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} orientation="vertical">
          <Step>
            <StepLabel>Allow pop-ups for this site</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Opening a tab from a background event is blocked by default. It works only once
                this origin is allowlisted. Detected browser: {guide.name}.
              </Typography>
              <Stack component="ol" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                {guide.steps.map((line) => (
                  <Typography key={line} component="li" variant="body2">
                    {line}
                  </Typography>
                ))}
              </Stack>
              <Button size="small" sx={{ mt: 2 }} variant="contained" onClick={() => setStep(1)}>
                Done, test it
              </Button>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Verify with a live test</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This fires a real pop-up from a timer, not from your click, so it faces the same
                conditions a detection does. Anything less would not prove anything.
              </Typography>
              {result === 'fail' ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Still blocked. Auto-open stays off, and detections will send a notification
                  instead. That fallback works; it is just one tap slower.
                </Alert>
              ) : null}
              <Button size="small" variant="contained" onClick={runTest}>
                Run the test
              </Button>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Allow notifications for the fallback</StepLabel>
            <StepContent>
              <Alert severity="success" sx={{ mb: 1 }}>
                Auto-open verified and enabled.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Notifications are the fallback when a tab is blocked or the per-minute cap is
                reached. Current permission: {permission}.
              </Typography>
              <Button size="small"
                variant="outlined"
                onClick={async () => setPermission(await requestNotificationPermission())}
                disabled={permission === 'granted'}
              >
                {permission === 'granted' ? 'Already granted' : 'Allow notifications'}
              </Button>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
